/**
 * DXF `\U+XXXX` unicode escaping / decoding (T9 PR-3 — Cyrillic mojibake fix).
 *
 * ## The bug
 *
 * The R2000 export declares HEADER `$DWGCODEPAGE = ANSI_1252` (Windows-1252)
 * with `$ACADVER = AC1015`. AutoCAD decodes the text bytes of a pre-R2007
 * (< AC1021) file as the declared codepage. Our serializer emits a UTF-8
 * byte stream at the IO boundary (`new Blob([dxfText])` → UTF-8). A Cyrillic
 * char written raw as UTF-8 (e.g. `А` U+0410 → bytes 0xD0 0x90) is then read
 * back by AutoCAD as two cp1252 chars → mojibake ("кракозябры").
 *
 * ## The fix (ezdxf-faithful)
 *
 * ezdxf's `dxf_backslash_replace` codec (ezdxf/lldxf/encoding.py) escapes every
 * character NOT representable in the file's target codepage as `\U+XXXX`
 * (4 hex digits for BMP, 8 for astral) — a pure-ASCII inline format code that
 * AutoCAD's text engine decodes back to the real character regardless of the
 * file codepage. We emit the SAME escape on the R2000 write path.
 *
 * ### Why we escape every NON-ASCII char (not only non-cp1252)
 *
 * ezdxf writes the whole file in the cp1252 codec, so a cp1252-representable
 * non-ASCII char (e.g. `é` U+00E9, `€` U+20AC) is written as a single cp1252
 * byte and round-trips fine WITHOUT an escape. Our IO boundary is UTF-8, so
 * that same `é` becomes the two UTF-8 bytes 0xC3 0xA9, which AutoCAD (reading
 * cp1252) renders as `Ã©` — mojibake. Proven with an ezdxf oracle: feeding a
 * UTF-8 stream that keeps `é` raw + escapes only `№` back through
 * `bytes.decode("cp1252")` + `decode_dxf_unicode` yields `cafÃ© №7` (WRONG),
 * whereas escaping BOTH yields `café №7` (CORRECT). So for a UTF-8 boundary the
 * only byte-safe set is pure ASCII → escape every code point > 0x7F.
 *
 * The precise cp1252-representability predicate is retained and exported as a
 * building block ({@link isCp1252Representable}) — it is the faithful predicate
 * for a future cp1252-encoded write path (or a writer-encoding selector). The
 * default escape predicate used on the UTF-8 path is "non-ASCII".
 *
 * Reference: https://ezdxf.readthedocs.io/en/stable/dxfinternals/fileencoding.html
 */

/**
 * The 27 specific code points mapped in the Windows-1252 0x80–0x9F range
 * (the bytes that differ from ISO-8859-1 / Latin-1). Source: Unicode
 * `CP1252.TXT` / WHATWG `windows-1252` index. The 5 undefined bytes
 * (0x81, 0x8D, 0x8F, 0x90, 0x9D) are intentionally absent — they have no
 * character and are NOT representable.
 *
 * NOTE: Node's `TextDecoder("windows-1252")` is NOT a faithful cp1252 codec —
 * it maps 0x80–0x9F as plain Latin-1 control chars (0x80→U+0080 etc.), so it
 * cannot be used as the cp1252 oracle. Hence this explicit table.
 */
const CP1252_HIGH_CODEPOINTS: readonly number[] = [
  0x20ac, // € EURO SIGN                       (0x80)
  0x201a, // ‚ SINGLE LOW-9 QUOTATION MARK     (0x82)
  0x0192, // ƒ LATIN SMALL LETTER F WITH HOOK  (0x83)
  0x201e, // „ DOUBLE LOW-9 QUOTATION MARK     (0x84)
  0x2026, // … HORIZONTAL ELLIPSIS             (0x85)
  0x2020, // † DAGGER                          (0x86)
  0x2021, // ‡ DOUBLE DAGGER                   (0x87)
  0x02c6, // ˆ MODIFIER LETTER CIRCUMFLEX      (0x88)
  0x2030, // ‰ PER MILLE SIGN                  (0x89)
  0x0160, // Š LATIN CAPITAL S WITH CARON      (0x8A)
  0x2039, // ‹ SINGLE LEFT ANGLE QUOTATION     (0x8B)
  0x0152, // Œ LATIN CAPITAL LIGATURE OE       (0x8C)
  0x017d, // Ž LATIN CAPITAL Z WITH CARON      (0x8E)
  0x2018, // ' LEFT SINGLE QUOTATION MARK      (0x91)
  0x2019, // ' RIGHT SINGLE QUOTATION MARK     (0x92)
  0x201c, // " LEFT DOUBLE QUOTATION MARK      (0x93)
  0x201d, // " RIGHT DOUBLE QUOTATION MARK     (0x94)
  0x2022, // • BULLET                          (0x95)
  0x2013, // – EN DASH                         (0x96)
  0x2014, // — EM DASH                         (0x97)
  0x02dc, // ˜ SMALL TILDE                     (0x98)
  0x2122, // ™ TRADE MARK SIGN                 (0x99)
  0x0161, // š LATIN SMALL S WITH CARON        (0x9A)
  0x203a, // › SINGLE RIGHT ANGLE QUOTATION    (0x9B)
  0x0153, // œ LATIN SMALL LIGATURE OE         (0x9C)
  0x017e, // ž LATIN SMALL Z WITH CARON        (0x9E)
  0x0178, // Ÿ LATIN CAPITAL Y WITH DIAERESIS  (0x9F)
];

const CP1252_HIGH_SET: ReadonlySet<number> = new Set(CP1252_HIGH_CODEPOINTS);

/**
 * `true` iff `codePoint` is representable in Windows-1252 (cp1252):
 *   - U+0000–U+007F  (ASCII)
 *   - U+00A0–U+00FF  (Latin-1 upper range — identical byte mapping)
 *   - the 27 specific {@link CP1252_HIGH_CODEPOINTS} in the 0x80–0x9F band
 *
 * Critically NOT a simple `codePoint <= 0xFF` check: the 0x80–0x9F band maps
 * to the special-punctuation set above, not to the raw byte value, and the
 * 5 undefined bytes are excluded.
 */
export function isCp1252Representable(codePoint: number): boolean {
  if (codePoint <= 0x7f) return true; // ASCII
  if (codePoint >= 0xa0 && codePoint <= 0xff) return true; // Latin-1 upper
  return CP1252_HIGH_SET.has(codePoint); // 0x80–0x9F specials
}

/** `true` iff `codePoint` is plain 7-bit ASCII (the byte-safe set for UTF-8). */
function isAscii(codePoint: number): boolean {
  return codePoint <= 0x7f;
}

/**
 * Format a single UTF-16 CODE UNIT (`charCodeAt`, 0x0000–0xFFFF) as an AutoCAD
 * `\U+XXXX` inline escape — EXACTLY 4 uppercase hex digits, always 7 chars.
 *
 * AutoCAD's `\U+XXXX` escape is canonically **4 hex digits = one UTF-16 code
 * unit**. There is NO 8-digit form: a supplementary-plane (astral) character is
 * a SURROGATE PAIR of two UTF-16 code units, so it is written as TWO `\U+XXXX`
 * escapes (the high surrogate then the low surrogate). The decoder reconstructs
 * the astral char from consecutive surrogate escapes via JS UTF-16 string
 * concatenation. This is why {@link escapeDxfUnicode} iterates by code UNIT, not
 * by code POINT — emitting one 4-hex escape per unit.
 *
 * Hex is UPPERCASE. ezdxf's WRITER emits lowercase, but its own DECODER regex
 * (`\U\+[A-F0-9]{4}`) only matches UPPERCASE — so uppercase is the strictly
 * more interoperable form (decodes in both ezdxf and AutoCAD). AutoCAD reads
 * `\U+` case-insensitively.
 */
function formatUnicodeEscape(codeUnit: number): string {
  return "\\U+" + codeUnit.toString(16).toUpperCase().padStart(4, "0");
}

/**
 * Escape every UTF-16 code UNIT for which `shouldEscape(unit)` returns `true` as
 * a `\U+XXXX` inline escape (exactly 4 uppercase hex digits); all other units
 * pass through verbatim.
 *
 * The default predicate is "non-ASCII" — the byte-safe set for our UTF-8 IO
 * boundary (see the module header). Iterates by UTF-16 code UNIT (`charCodeAt`,
 * NOT `for…of`/`codePointAt`) because AutoCAD's `\U+XXXX` is canonically one
 * escape per code unit: an astral char's surrogate PAIR becomes TWO `\U+`
 * escapes (high surrogate + low surrogate). The decoder reconstructs the astral
 * char by concatenating the two surrogate units back into one JS string.
 *
 * Idempotent on already-escaped / pure-ASCII text: the output is pure ASCII, so
 * re-running it is a no-op. This makes it safe to apply at more than one site
 * (e.g. MTEXT escape-then-chunk followed by a defensive whole-document pass).
 */
export function escapeDxfUnicode(
  s: string,
  shouldEscape: (codeUnit: number) => boolean = DEFAULT_SHOULD_ESCAPE,
): string {
  // Defensive: a write chokepoint may hand us a non-string group value (a
  // numeric/undefined value under an allowlisted code) — never crash, return
  // the input verbatim so the caller serializes it unchanged.
  if (typeof s !== "string") return s;

  // Fast path: ASCII-only strings (the overwhelming majority — handles,
  // coords, structural markers, Latin text) need no work. ONLY valid for the
  // DEFAULT "escape non-ASCII" predicate: a custom predicate may escape ASCII
  // units (e.g. `() => true`), so it must always iterate.
  if (shouldEscape === DEFAULT_SHOULD_ESCAPE) {
    let needsEscape = false;
    for (let i = 0; i < s.length; i += 1) {
      if (s.charCodeAt(i) > 0x7f) {
        needsEscape = true;
        break;
      }
    }
    if (!needsEscape) return s;
  }

  let out = "";
  for (let i = 0; i < s.length; i += 1) {
    const unit = s.charCodeAt(i);
    out += shouldEscape(unit) ? formatUnicodeEscape(unit) : s[i];
  }
  return out;
}

/**
 * The default escape predicate — "escape every non-ASCII code unit", the
 * byte-safe set for our UTF-8 IO boundary. Hoisted to a named constant (not an
 * inline default) so {@link escapeDxfUnicode} can detect "caller did NOT pass a
 * custom predicate" by identity (`shouldEscape === DEFAULT_SHOULD_ESCAPE`) and
 * gate the ASCII fast path behind it — a custom predicate (e.g. `() => true`)
 * may escape ASCII units, so for one the function must always iterate.
 */
const DEFAULT_SHOULD_ESCAPE = (u: number): boolean => !isAscii(u);

/**
 * Matches an AutoCAD `\U+` escape: literal `\U+` then EITHER 8 hex digits OR 4
 * hex digits (the 8-branch is tried first, so a present second quad is captured),
 * case-insensitive (AutoCAD may emit either case; we WRITE uppercase). The single
 * capture group holds the whole 4- or 8-char hex run; {@link decodeDxfUnicode}
 * disambiguates the 8-hex case by VALIDITY.
 *
 * The 8-OR-4 ALTERNATION (rather than a `{4}` + optional-`{4}`) is deliberate: a
 * fixed-length alternation has no nested quantifier, so it side-steps the
 * `safe-regex` heuristic AND cannot backtrack catastrophically (both branches are
 * fixed length over the same disjoint-free class — a non-issue for ReDoS).
 *
 * Our WRITER never emits 8 digits — an astral char is written as its surrogate
 * PAIR of two 4-hex escapes (high + low), each matched by the 4-branch and decoded
 * one unit at a time. The 8-branch exists ONLY so the DECODER also accepts a
 * foreign 8-digit astral escape; it never affects writes.
 *
 * Disambiguation (in the replace callback, not the regex): when 8 hex are present
 * we decode them as ONE astral code point ONLY if the value is a valid astral char
 * (0x10000–0x10FFFF). Otherwise we decode just the FIRST 4 hex and RE-EMIT the
 * trailing 4 hex as literal text — so `\U+00E90000` (é followed by literal "0000")
 * still decodes to "é0000", not an invalid 0x00E90000 code point.
 */
const DXF_UNICODE_ESCAPE = /\\U\+([0-9A-Fa-f]{8}|[0-9A-Fa-f]{4})/gi;

/** Inclusive lower bound of the Unicode supplementary (astral) planes. */
const ASTRAL_MIN = 0x10000;
/** Inclusive upper bound of the Unicode code space (highest astral code point). */
const ASTRAL_MAX = 0x10ffff;

/**
 * Decode every `\U+` escape in `s` back to its character (inverse of
 * {@link escapeDxfUnicode}). Case-insensitive. Non-escape backslash sequences
 * (MTEXT formatting codes such as `\P`, `\f`, `\C1;`, `{}`) are left untouched
 * because the regex only matches the strict `\U+<8 hex|4 hex>` shape.
 *
 * ## Two escape widths, disambiguated by VALIDITY
 *
 * Our WRITER emits 4 hex per UTF-16 code UNIT — an astral char is its surrogate
 * PAIR of two consecutive 4-hex escapes (`\U+D83D\U+DE00` for 😀). Each 4-hex
 * escape decodes to ONE UTF-16 code unit via `String.fromCharCode`; the two
 * adjacent surrogate units are presented by the JS string runtime as the single
 * astral char, so an astral round-trip (e.g. "𝐀" U+1D400, "😀" U+1F600) is exact.
 *
 * Foreign producers, however, may emit the 8-digit astral form (`\U+0001F600`).
 * The regex therefore matches EITHER 8 hex OR 4 hex (8-branch first). The callback
 * disambiguates the 8-hex case by VALIDITY:
 *   - 8 hex whose value is a valid astral code point (0x10000–0x10FFFF) →
 *     decode as that ONE astral code point (`String.fromCodePoint`).
 *   - otherwise (only 4 hex; OR 8 hex whose value is > 0x10FFFF such as the
 *     ambiguity case `\U+00E90000` = é + literal "0000"; OR 8 hex < 0x10000)
 *     → decode the FIRST 4 hex via `String.fromCharCode` and RE-EMIT the trailing
 *     4 hex (if any) as literal text. The regex consumes 8 chars but only 4 are
 *     decoded — the trailing hex is returned verbatim from the callback.
 *
 * This keeps the `\U+XXXX`-then-literal-hex case correct (`\U+00E90000` → "é0000"
 * — the value 0x00E90000 is NOT a valid code point, so it never reaches
 * `fromCodePoint`) WHILE accepting a genuine foreign 8-digit astral escape.
 *
 * Idempotent: decoded output contains no `\U+` escape, so re-running is a no-op.
 */
export function decodeDxfUnicode(s: string): string {
  // Defensive: a caller may hand us a non-string group value — return verbatim.
  if (typeof s !== "string") return s;
  if (s.indexOf("\\U+") === -1 && s.indexOf("\\u+") === -1) return s;
  return s.replace(DXF_UNICODE_ESCAPE, (_m, hex: string) => {
    if (hex.length === 8) {
      // 8 hex present: decode as a single astral code point ONLY when valid.
      const cp = Number.parseInt(hex, 16);
      if (cp >= ASTRAL_MIN && cp <= ASTRAL_MAX) {
        return String.fromCodePoint(cp);
      }
      // Invalid 8-digit value (e.g. 0x00E90000 = é + literal "0000", or any
      // value < 0x10000): decode only the FIRST 4 hex and re-emit the trailing
      // 4 hex as literal text. The regex consumed 8 chars; only 4 decode.
      return (
        String.fromCharCode(Number.parseInt(hex.slice(0, 4), 16)) + hex.slice(4)
      );
    }
    // Plain 4-hex escape: one UTF-16 code unit (surrogate-pair-safe).
    return String.fromCharCode(Number.parseInt(hex, 16));
  });
}

/**
 * The XDATA group codes whose VALUE is a free-form STRING that the writer
 * chokepoint `\U+`-escapes: 1000 (string), 1001 (appid), 1003 (layer name).
 * All other XDATA codes carry binary (1004), a handle (1005), control braces
 * (1002), or numbers (1010–1071) — never escaped on write, so never decoded on
 * read. Kept in sync with the 1000/1001/1003 entries of
 * {@link STRING_CONTENT_GROUP_CODES}.
 */
const XDATA_STRING_CODES: ReadonlySet<number> = new Set([1000, 1001, 1003]);

/**
 * Decode the VALUE of a single XDATA group at the COLLECTOR — return a group
 * whose value is `decodeDxfUnicode`d iff its code is a string-content XDATA code
 * (1000/1001/1003); every other code passes through untouched.
 *
 * T9 PR-3 — the writer escapes XDATA 1000/1001/1003 (they are in the write
 * allowlist), but every XDATA collector historically stored the value VERBATIM,
 * so a consumer that matches an XDATA layer / name ref against the DECODED
 * drawing-model names (e.g. `readViewportOverrides` frozen-layer match) sees a
 * literal `\U+…` after export→reimport and the match breaks. Decoding at the
 * COLLECTOR makes EVERY XDATA consumer see decoded text — a single by-construction
 * choke-point instead of a per-consumer sweep.
 *
 * Verbatim re-export stays byte-identical: decode-on-read (real char) →
 * re-escape-on-write at the chokepoint (`\U+…`) yields the same bytes; an
 * ASCII-only XDATA value (the #384/#394/#388 byte-identical fixtures) is a no-op
 * on both sides because `decodeDxfUnicode` returns the input when no `\U+` is
 * present. Returns a NEW group object when a decode actually changes the value
 * (never mutates the caller-owned `DxfGroup`, mirroring the writer's no-mutate
 * principle); returns the SAME reference when nothing changed.
 */
export function decodeXdataGroupValue<T extends { code: number; value: string }>(
  group: T,
): T {
  if (!XDATA_STRING_CODES.has(group.code)) return group;
  // Defensive: a non-string value under a string-content code passes through.
  if (typeof group.value !== "string") return group;
  const decoded = decodeDxfUnicode(group.value);
  if (decoded === group.value) return group;
  return { ...group, value: decoded };
}

/**
 * Split an ALREADY-ESCAPED (pure-ASCII) string into chunks of at most
 * `maxLen` characters WITHOUT ever splitting a `\U+XXXX` escape across a
 * boundary. Used by the MTEXT writer: escape the full contents first, then
 * chunk the escaped string into the 250-char group-3 segments + final group 1.
 *
 * A `\U+XXXX` escape is exactly 7 chars (`\U+` + 4 hex); cutting it ANYWHERE
 * mid-sequence — after the leading `\`, after `\U`, after `\U+`, or mid-hex —
 * would corrupt the text on read. When a chunk boundary would land inside an
 * escape we back the cut up to the escape's start so the whole escape moves to
 * the next chunk.
 */
export function chunkEscapedString(s: string, maxLen: number): string[] {
  // Defensive: a non-string input passes through as an empty chunk list rather
  // than crashing on `.length` / `.slice`.
  if (typeof s !== "string") return [];
  // A `\U+XXXX` escape is exactly ESCAPE_LEN (7) chars; a chunk shorter than
  // that cannot hold one intact, so maxLen < ESCAPE_LEN is never satisfiable.
  // (MTEXT passes 250, so no real caller breaks.)
  if (maxLen < ESCAPE_LEN)
    throw new Error(
      `chunkEscapedString: maxLen must be >= ${ESCAPE_LEN} (one \\U+XXXX escape); got ${maxLen}`,
    );
  const chunks: string[] = [];
  let i = 0;
  while (i < s.length) {
    let end = Math.min(i + maxLen, s.length);
    if (end < s.length) {
      // Pull the cut back if it lands inside a `\U+XXXX` escape. Never past the
      // chunk start (a single escape always fits in maxLen ≥ ESCAPE_LEN).
      const adjusted = avoidEscapeSplit(s, i, end);
      if (adjusted > i) end = adjusted;
    }
    chunks.push(s.slice(i, end));
    i = end;
  }
  return chunks;
}

/**
 * Given a tentative cut at `end`, return the largest cut ≤ `end` (and > `start`)
 * that does not fall inside a `\U+XXXX` escape. If the cut is already clean it
 * is returned unchanged.
 *
 * A cut at offset `end` falls inside an escape iff some escape starts at
 * `escStart` with `escStart < end < escStart + ESCAPE_LEN`. Because the escape
 * is `ESCAPE_LEN` chars and starts with the literal `\U+`, an interior cut can
 * land at offsets `escStart+1 .. escStart+6` — i.e. after `\`, after `\U`,
 * after `\U+`, or mid-hex. We therefore scan back from `end` up to
 * `ESCAPE_LEN-1` positions looking for an escape START (`\U+`); the FIRST one
 * found is the closest-to-`end` candidate, and if the cut bisects it we move the
 * cut to its start. (Scanning only `ESCAPE_LEN-1` back is sufficient: an escape
 * whose start is ≥ ESCAPE_LEN before `end` is wholly before the cut and cannot
 * be bisected.)
 */
function avoidEscapeSplit(s: string, start: number, end: number): number {
  // Earliest position an escape that the cut could bisect may start at.
  const scanFrom = Math.max(start, end - (ESCAPE_LEN - 1));
  for (let escStart = end - 1; escStart >= scanFrom; escStart -= 1) {
    // Is position escStart the start of a `\U+XXXX` escape?
    if (
      s[escStart] === "\\" &&
      (s[escStart + 1] === "U" || s[escStart + 1] === "u") &&
      s[escStart + 2] === "+" &&
      DXF_UNICODE_ESCAPE_ANCHORED.test(s.slice(escStart, escStart + ESCAPE_LEN))
    ) {
      const escEnd = escStart + ESCAPE_LEN;
      // Cut bisects the escape (escStart < end < escEnd) → move cut to escStart.
      if (end > escStart && end < escEnd) {
        return escStart > start ? escStart : end;
      }
      // Closest escape start to `end` is clean w.r.t. the cut; stop scanning —
      // any earlier escape is even further from `end` and also clean.
      break;
    }
  }
  return end;
}

/**
 * Anchored 4-hex variant for single-escape probing inside {@link chunkEscapedString}.
 * Deliberately 4-hex-ONLY (not the optional-8 decode regex): chunking runs on the
 * WRITER's output, which emits exactly one 4-hex escape per UTF-16 code unit
 * (ESCAPE_LEN = 7 chars). It never sees a foreign 8-digit astral escape, so the
 * 7-char window probe stays exact. (Decode-side 8-digit handling lives in
 * {@link decodeDxfUnicode}, a read-only concern.)
 */
const DXF_UNICODE_ESCAPE_ANCHORED = /^\\U\+([0-9A-Fa-f]{4})/i;

/** Length of one `\U+XXXX` escape: `\` `U` `+` + 4 hex digits = 7 chars. */
const ESCAPE_LEN = 7;

/** Exposed for unit tests — the canonical cp1252 0x80–0x9F code-point table. */
export const CP1252_SPECIAL_CODEPOINTS = CP1252_HIGH_CODEPOINTS;

/**
 * DXF group codes whose VALUE is free-form string content that may carry
 * non-ASCII characters and therefore must be `\U+`-escaped on the legacy
 * write path. Used as the allowlist at the serializer chokepoint so handles
 * (5/105/330/350/360/1005 — always hex ASCII), numeric/coordinate codes
 * (10–59, 60–79, 90–99, 140–149, 210–239, 1010–1071 — always ASCII numbers),
 * and structural markers are NEVER transformed.
 *
 * Per the Autodesk DXF reference, string-value group codes are:
 *   - 0       entity / table / section type names (ASCII keywords — escaping
 *             them is a harmless no-op, but they ARE string-typed)
 *   - 1       primary text (TEXT/MTEXT/ATTRIB value/ATTDEF default/DIMENSION
 *             override text / XRECORD), 2 names (layer/block/style/linetype/
 *             dimstyle/blockrecord/ATTRIB tag/hatch pattern), 3–4 names &
 *             descriptions (MTEXT chunk/ATTDEF prompt/DIMENSION style/font
 *             file/linetype description/dictionary entry/style font),
 *             5 is a HANDLE (excluded), 6 linetype name, 7 text-style name,
 *             8 layer name, 9 header var name
 *   - 100/102/105 control strings (105 is a HANDLE — excluded)
 *   - 300–309 arbitrary text
 *   - 340–344 are HANDLE references in pure AutoCAD DXF (DIMTXSTY / DIMLDRBLK /
 *             DIMBLK / DIMBLK1 / DIMBLK2 — a hex BLOCK_RECORD/STYLE handle), but
 *             THIS repo also round-trips them as plain NAME strings: the DIMSTYLE
 *             writer (`serializeDimStyleEntry`) emits the built-in/custom arrow &
 *             text-style NAME there (AutoCAD tolerates built-in arrow block
 *             names), and the MLEADER body writer emits a block / arrowhead NAME
 *             into 342 (arrow_head_handle) / 344 (block_record_handle) as a
 *             model-authored fallback. So a Cyrillic name in those slots MUST be
 *             escaped. Escaping is SAFE for the handle-valued uses too: a hex
 *             handle is pure ASCII → `escapeDxfUnicode` is a no-op. The symmetric
 *             read-side decode already lives at the NAME-valued readers
 *             (`parseDimStyleEntry` decodes 340-344; the MLEADER `internTrim`
 *             reader decodes 342/344); handle-valued reads (MLEADERSTYLE 340-343,
 *             CONTEXT_DATA 340) leave decode off — a no-op on a hex handle and we
 *             must not touch handle resolution.
 *   - 410–419 plot/layout strings (NOTE: 420–429 are 32-bit true-colour
 *             INTEGERS — excluded)
 *   - 430–439 colour NAME / book strings
 *   - 470–479 strings
 *   - 999     comment
 *   - 1000    XDATA string, 1001 XDATA appid, 1003 XDATA layer name
 *
 * The non-string sub-ranges inside the above bands (e.g. 105, 5, the 420–429
 * true-colour ints) are explicitly excluded below. We list codes individually
 * rather than by range so the exclusions are by-construction visible.
 */
const STRING_CONTENT_CODES: readonly number[] = [
  0, 1, 2, 3, 4, 6, 7, 8, 9, 100, 102, 300, 301, 302, 303, 304, 305, 306, 307,
  308, 309, 340, 341, 342, 343, 344, 410, 411, 412, 413, 414, 415, 416, 417,
  418, 419, 430, 431, 432, 433, 434, 435, 436, 437, 438, 439, 470, 471, 472,
  473, 474, 475, 476, 477, 478, 479, 999, 1000, 1001, 1003,
];

/**
 * Set membership form of {@link STRING_CONTENT_CODES} for the serializer
 * chokepoint. A group is escaped iff its code is in this set AND escaping is
 * enabled for the target DXF version.
 */
export const STRING_CONTENT_GROUP_CODES: ReadonlySet<number> = new Set(
  STRING_CONTENT_CODES,
);
